import { addRewards, removeReward, getBasketRewards } from '@koala/sdk/v4';
import { call, put, select, takeLatest, type SagaReturnType } from 'redux-saga/effects';
import actions from './actions';
import { BASKET_REWARDS_ERROR_STATUS } from '@/constants/basketRewards';
import { LOYALTY_FEATURES } from '@/constants/loyalty';
import basketActions from '@/redux/basket/actions';
import orderStatusActions from '@/redux/orderStatus/actions';
import { createHttpClient } from '@/services/client';
import { type RootState } from '@/types/app';
import { OrderStatusMessages } from '@/types/orderStatus';
import { getOrigin } from '@/utils';
import { getIdsFromState } from '@/utils/checkout';
import { prepareErrorMessage } from '@/utils/global';
import { getLoyaltyInfoFromState } from '@/utils/loyalty';
import * as ErrorReporter from '@/utils/errorReporter';
import { ERROR_MESSAGES } from '@/constants/events';
import { isStatusError, GATEWAY_STATUSES } from '@/utils/networkErrors';
import { retryOnceOnStatuses, retryWithBackoff } from '@/utils/requests';

/** Get basket rewards */
export function* getRewardsSaga() {
  yield put(orderStatusActions.basketRewardsGet());
  yield put(actions.setLoadingRewards(true));

  try {
    const state: RootState = yield select();
    const organization = state.app.organization;

    // If loyalty is enabled
    if (organization.organization.loyalty_driver_id) {
      // Only fetch available rewards if the loyalty provider supports it
      const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
        yield select(getLoyaltyInfoFromState);
      if (!loyaltyFeatureFlags?.[LOYALTY_FEATURES.GET_AVAILABLE_REWARDS]) {
        return;
      }

      const { basketId, locationId } = getIdsFromState(state);
      const client = createHttpClient({
        origin: getOrigin(window.location.host),
      });
      const availableRewards: SagaReturnType<typeof getBasketRewards> = yield* retryWithBackoff(
        getBasketRewards,
        {},
        { basketId, locationId },
        { client },
      );

      yield put(actions.setAvailableRewards(availableRewards));
    }
  } catch (error) {
    // TEAM-14558: flag the failure so the checkout rewards component can distinguish
    // "fetch failed" from "genuinely no rewards" and stop showing the false
    // "You don't have any rewards yet" copy.
    yield put(actions.setRewardsFetchFailed(true));

    if (isStatusError(error, GATEWAY_STATUSES)) {
      yield put(orderStatusActions.recovery502Triggered('rewards_available'));
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.FETCH_REWARDS_ERROR,
      });
      return;
    }

    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      null,
      error,
    );
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.FETCH_REWARDS_ERROR,
    });
  } finally {
    yield put(actions.setLoadingRewards(false));
    yield put(orderStatusActions.orderPending());
  }
}

/**
 * Add basket rewards
 *
 */
export function* addRewardSaga(action: ReturnType<typeof actions.addReward>) {
  yield put(orderStatusActions.basketRewardsAdd());
  yield put(actions.setPendingReward(action?.reward?.id));
  try {
    const state: RootState = yield select();
    const { basketId, locationId } = getIdsFromState(state);

    // Get loyalty feature flags
    const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
      yield select(getLoyaltyInfoFromState);

    // Check if loyalty provider supports applying/destroying a reward or getting all applied rewards
    if (
      !loyaltyFeatureFlags?.[LOYALTY_FEATURES.APPLY_REWARDS] ||
      !loyaltyFeatureFlags?.[LOYALTY_FEATURES.GET_APPLIED_REWARDS] ||
      !loyaltyFeatureFlags?.[LOYALTY_FEATURES.DESTROY_REWARD]
    ) {
      yield put(orderStatusActions.orderPending());
      return;
    }

    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    const response: SagaReturnType<typeof addRewards> = yield* retryOnceOnStatuses(
      addRewards,
      { statuses: GATEWAY_STATUSES },
      {
        basketId,
        rewardCodes: [action.reward.id.toString()],
        locationId,
        validate: true,
      },
      { client },
    );
    yield put(basketActions.success(response));
  } catch (error) {
    if (isStatusError(error, GATEWAY_STATUSES)) {
      yield put(orderStatusActions.recovery502Triggered('rewards'));
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.ADD_REWARDS_FAILURE,
      });
      yield put(actions.setPendingReward(null));
      yield put(orderStatusActions.orderPending());
      return;
    }

    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      OrderStatusMessages.BASKET_REWARDS_ADDING_ERROR,
      error,
    );

    if (errorResponse?.error?.error === BASKET_REWARDS_ERROR_STATUS.UNAVAILABLE) {
      yield put(actions.setUnavailableReward(action.reward));
    } else {
      // TEAM-13882: `errorResponse.message` is the resolved diner copy from
      // `resolveDinerMessage`. For code 29 (NO_MATCHING_REWARD_ITEM), tier 1
      // returns "Your order doesn't include an item that qualifies for this
      // reward." — no special branch needed. Never render `error_description`
      // directly; it is raw upstream text (the render rule's last tier: never displayed).
      yield put(orderStatusActions.orderStatusMessageErrorSet(errorResponse.message));
    }

    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.ADD_REWARDS_FAILURE,
    });
  }

  // Reset order status
  yield put(actions.setPendingReward(null));
  yield put(orderStatusActions.orderPending());
}

export function* removeRewardSaga(action: ReturnType<typeof actions.removeReward>) {
  yield put(orderStatusActions.basketRewardsRemove());

  try {
    const state: RootState = yield select();
    const { basketId, locationId } = getIdsFromState(state);

    // Get loyalty feature flags
    const { loyaltyFeatureFlags }: ReturnType<typeof getLoyaltyInfoFromState> =
      yield select(getLoyaltyInfoFromState);

    // Check if loyalty provider supports destroying a reward or getting all applied rewards
    if (
      !loyaltyFeatureFlags?.[LOYALTY_FEATURES.GET_APPLIED_REWARDS] ||
      !loyaltyFeatureFlags?.[LOYALTY_FEATURES.DESTROY_REWARD]
    ) {
      yield put(orderStatusActions.orderPending());
      return;
    }

    const client = createHttpClient({
      origin: getOrigin(window.location.host),
    });
    yield* retryOnceOnStatuses(
      removeReward,
      { statuses: GATEWAY_STATUSES },
      { basketId, rewardCode: action.reward.id.toString(), locationId },
      { client },
    );

    // Success
    yield put(orderStatusActions.orderPending());
    yield put(actions.resetUnavailableRewards());

    // Silently validate basket
    yield put(orderStatusActions.validateOrder(basketId, locationId));
  } catch (error) {
    if (isStatusError(error, GATEWAY_STATUSES)) {
      yield put(orderStatusActions.recovery502Triggered('rewards'));
      ErrorReporter.captureException(error, 'error', {
        eventName: ERROR_MESSAGES.REMOVE_REWARDS_FAILURE,
      });
      yield put(orderStatusActions.orderPending());
      return;
    }

    // Error Notification
    const errorResponse: SagaReturnType<typeof prepareErrorMessage> = yield call(
      prepareErrorMessage,
      OrderStatusMessages.BASKET_REWARDS_REMOVING_ERROR,
      error,
    );
    yield put(orderStatusActions.orderStatusMessageErrorSet(errorResponse.message));

    // KA event
    ErrorReporter.captureException(error, 'error', {
      errorResponse,
      eventName: ERROR_MESSAGES.REMOVE_REWARDS_FAILURE,
    });
  }

  yield put(orderStatusActions.orderPending());
}

export default function* rootSaga() {
  yield takeLatest(actions.GET_REWARDS, getRewardsSaga);
  yield takeLatest(actions.ADD_REWARD, addRewardSaga);
  yield takeLatest(actions.REMOVE_REWARD, removeRewardSaga);
}
